/**
 * Appeal manager ("Apelyasiya meneceri") data layer.
 *
 * Organizer, jury members and teams are anonymous: each enters a code, gets a room
 * token back and sends it as `X-Appeal-Token`. The server decides what each role may
 * see (see AppealRoomsController / AppealRoomService in the API). Live updates come
 * through the `/appealRoomHub` SignalR hub as a payload-less `roomChanged` event.
 */
import axios, { AxiosError } from 'axios';
import * as signalR from '@microsoft/signalr';
import { apiUrl } from '../const/const';

export type AppealRole = 'owner' | 'team' | 'jury';
export type QuestionKind = 'single' | 'duplet' | 'blitz';
export type HandoutKind = 'text' | 'image';
/** What the team asks for. */
export type AppealKind = 'removeQuestion' | 'acceptAnswer';

export interface AppealHandout {
  kind: HandoutKind;
  text: string;
  /** File name returned by `uploadHandout`. */
  image: string;
  /** Path under the site's images/ folder (read-only, set by the server). */
  url?: string;
}

export interface AppealQuestionPart {
  text: string;
  answer: string;
  /** This part's own handout; unused when the question has a shared handout. */
  handout: AppealHandout | null;
}

/** Same fields as the admin NHN (Type 1) form. */
export interface AppealQuestion {
  id: number;
  /** The code teams use to appeal this question. Owner only. */
  code?: string | null;
  number: number;
  kind: QuestionKind;
  isTranslated: boolean;
  noteBefore: string;
  /** Duplet/blitz: one handout for all parts. */
  sharedHandout: AppealHandout | null;
  /** One part for a regular question, 2 for a duplet, 3 for a blitz. */
  parts: AppealQuestionPart[];
  considered: string;
  comment: string;
  sources: string[];
  authors: string;
  createdAt: string;
}

export type AppealQuestionInput = Omit<AppealQuestion, 'id' | 'code' | 'createdAt'>;

export interface JuryVerdict {
  id: number;
  juryName: string;
  accepted: boolean;
  text: string;
  updatedAt: string;
}

export interface Appeal {
  id: number;
  questionId: number;
  /** Empty for the jury: they judge the argument, not the team. */
  teamName: string;
  kind: AppealKind;
  /** The team's answer, for `acceptAnswer`. */
  teamAnswer: string;
  /** Sanitized rich-text HTML. */
  text: string;
  /** Owner and team: a jury member has voted, so the team can no longer change it. */
  inReview: boolean;
  /** VERDICTS_TO_DECIDE jurors have voted, so the appeal is locked. */
  decided: boolean;
  /**
   * Owner and team, once decided. Removing the question needs all VERDICTS_TO_DECIDE jurors to agree;
   * counting the team's answer needs a majority.
   */
  decision: 'accepted' | 'rejected' | null;
  createdAt: string;
  updatedAt: string;
  /** Owner: every verdict. Jury: only their own. Team: every verdict, without juror names, once decided. */
  verdicts: JuryVerdict[];
}

export interface AppealParticipant {
  name: string;
  role: Exclude<AppealRole, 'owner'>;
  /** Teams only. */
  questionId: number | null;
  joinedAt: string;
}

export interface AppealRoom {
  id: string;
  name: string;
  isOpen: boolean;
  createdAt: string;
  role: AppealRole;
  me: string;
  /** Owner only. */
  juryCode: string | null;
  ownerCode: string | null;
  questions: AppealQuestion[];
  appeals: Appeal[];
  /** Owner only. */
  participants: AppealParticipant[];
}

export interface AppealSession {
  roomId: string;
  role: AppealRole;
  /** Team or jury member name; empty for the owner. */
  name: string;
  token: string;
}

export class AppealError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
  }
}

export const APPEAL_KIND_LABELS: Record<AppealKind, string> = {
  removeQuestion: 'Sualın çıxarılması üçün',
  acceptAnswer: 'Cavabımızın qəbul edilməsi üçün',
};

/** This many verdicts decide an appeal by majority; then it is locked. Mirrors the API. */
export const VERDICTS_TO_DECIDE = 3;

export const MAX_HANDOUT_IMAGE_BYTES = 2 * 1024 * 1024;

export const normalizeCode = (code: string) => code.trim().toUpperCase().replace(/[\s-]+/g, '');

/** 6 characters for team and jury codes, 8 for the organizer's code. */
export const isCodeShaped = (code: string) => /^[A-Z0-9]{6}$|^[A-Z0-9]{8}$/.test(normalizeCode(code));

// ─── HTTP ─────────────────────────────────────────────────────────────────────

// Own instance: room tokens, not the site login, authorize these calls
const http = axios.create({ baseURL: `${apiUrl}api/appeal-rooms` });

const headers = (session: AppealSession) => ({ 'X-Appeal-Token': session.token });

const toAppealError = (e: unknown): AppealError => {
  const err = e as AxiosError<{ message?: string }>;
  const status = err.response?.status;
  if (status === 429) return new AppealError('Çox sayda cəhd. Bir neçə dəqiqə sonra yenidən yoxlayın.', status);
  const message = err.response?.data?.message;
  if (message) return new AppealError(message, status);
  if (!err.response) return new AppealError('Serverlə əlaqə yoxdur. İnterneti yoxlayın.');
  return new AppealError('Xəta baş verdi. Yenidən cəhd edin.', status);
};

const call = async <T>(request: () => Promise<{ data: T }>): Promise<T> => {
  try {
    return (await request()).data;
  } catch (e) {
    throw toAppealError(e);
  }
};

// Writes answer with the fresh room, so screens update without waiting for the hub
const localListeners = new Set<(room: AppealRoom) => void>();
const write = async (request: () => Promise<{ data: AppealRoom }>) => {
  const room = await call(request);
  localListeners.forEach(listener => listener(room));
  return room;
};

/** Called with the room returned by every write made in this tab. */
export const onLocalRoomUpdate = (listener: (room: AppealRoom) => void) => {
  localListeners.add(listener);
  return () => {
    localListeners.delete(listener);
  };
};

// ─── Rooms & sessions ─────────────────────────────────────────────────────────

export const createRoom = (name: string) =>
  call<AppealSession>(() => http.post('', { name: name.trim() }));

/** Which room and role a code belongs to, so the lobby asks for a name only when needed. */
export const resolveCode = (code: string) =>
  call<{ roomName: string; role: AppealRole; questionNumber: number | null }>(() =>
    http.post('resolve', { code: normalizeCode(code) })
  );

export const joinRoom = (code: string, name: string) =>
  call<AppealSession>(() => http.post('join', { code: normalizeCode(code), name: name.trim() }));

export const getRoom = (session: AppealSession) =>
  call<AppealRoom>(() => http.get(session.roomId, { headers: headers(session) }));

export const setRoomOpen = (session: AppealSession, isOpen: boolean) =>
  write(() => http.patch(session.roomId, { isOpen }, { headers: headers(session) }));

export const deleteRoom = (session: AppealSession) =>
  call<void>(() => http.delete(session.roomId, { headers: headers(session) }));

/**
 * Calls `onChange` whenever someone changes the room. Also re-checks on reconnect and
 * every 30 s, in case an event was missed.
 */
export const subscribeToRoom = (session: AppealSession, onChange: () => void): (() => void) => {
  let stopped = false;
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(`${apiUrl}appealRoomHub`)
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  const watch = async () => {
    try {
      await connection.invoke('Watch', session.roomId, session.token);
    } catch {
      // Polling below still keeps the screen fresh
    }
  };

  connection.on('roomChanged', onChange);
  connection.onreconnected(async () => {
    await watch();
    onChange();
  });
  connection
    .start()
    .then(() => (stopped ? connection.stop() : watch()))
    .catch(() => undefined);

  const poll = window.setInterval(onChange, 30000);
  return () => {
    stopped = true;
    window.clearInterval(poll);
    connection.stop().catch(() => undefined);
  };
};

// Per tab, so an organizer can try the team or jury view in another tab of the same browser
const SESSION_KEY = 'appeals:session';

export const saveSession = (session: AppealSession | null) => {
  try {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Not remembering the session only means re-entering the code after a reload
  }
};

export const readSession = (): AppealSession | null => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null');
    return parsed && parsed.roomId && parsed.role && parsed.token ? parsed : null;
  } catch {
    return null;
  }
};

// ─── Questions (owner) ────────────────────────────────────────────────────────

/** Uploads a handout image and returns its file name and URL. */
export const uploadHandout = (session: AppealSession, file: File) => {
  const form = new FormData();
  form.append('image', file);
  return call<{ image: string; url: string }>(() =>
    http.post(`${session.roomId}/handouts`, form, { headers: headers(session) })
  );
};

export const addQuestion = (session: AppealSession, input: AppealQuestionInput) =>
  write(() => http.post(`${session.roomId}/questions`, input, { headers: headers(session) }));

export const updateQuestion = (session: AppealSession, questionId: number, input: AppealQuestionInput) =>
  write(() => http.put(`${session.roomId}/questions/${questionId}`, input, { headers: headers(session) }));

export const deleteQuestion = (session: AppealSession, questionId: number) =>
  write(() => http.delete(`${session.roomId}/questions/${questionId}`, { headers: headers(session) }));

// ─── Appeals (teams) ──────────────────────────────────────────────────────────

/** One appeal per team per question. It is final: it cannot be edited or withdrawn. */
export const submitAppeal = (
  session: AppealSession,
  appeal: { kind: AppealKind; teamAnswer: string; text: string }
) => write(() => http.post(`${session.roomId}/appeal`, appeal, { headers: headers(session) }));

// ─── Verdicts (jury) ──────────────────────────────────────────────────────────

/** One final verdict per jury member per appeal, until VERDICTS_TO_DECIDE jurors have voted. */
export const submitVerdict = (session: AppealSession, appealId: number, accepted: boolean, text: string) =>
  write(() =>
    http.put(`${session.roomId}/appeals/${appealId}/verdict`, { accepted, text }, { headers: headers(session) })
  );
